import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {createRef, ref, Ref} from "lit/directives/ref.js";
import {get} from "lit-translate";
import {container} from "tsyringe";

import type {BoardObject, PasteChoice, WhiteboardChangedEventDetail} from "@o2d/whiteboard";
import type {InjectedButton} from "@o2d/shared";

import {appState} from "../../../app.state";
import {BackendClient} from "../../../connectivity/backend-client";
import {LockConflictError} from "../../../connectivity/lock-conflict.error";
import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {deriveLocked} from "../../../domain/helpers/derive-locked.helper";
import {
    anotherHolderHoldsLock,
    EditLockHolder,
    registerEditLockHolder,
    unregisterEditLockHolder,
} from "../../../domain/helpers/edit-lock-holders.helper";
import {acquireKnowledgeObjectEditLock} from "../../../domain/helpers/acquire-knowledge-object-edit-lock.helper";
import {KnowledgeDraftChangeNotifier} from "../../../domain/helpers/knowledge-draft-registry.helper";
import {
    cancelPendingWhiteboardDraft,
    clearWhiteboardDraft,
    loadWhiteboardDraft,
    saveWhiteboardDraft,
    saveWhiteboardDraftNow,
} from "../../../domain/helpers/whiteboard-draft-store.helper";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {buildAssetEmbedSrc} from "../../../helpers/asset-embed.helper";
import {toggleKnowledgeFilterDrawer} from "../../../helpers/knowledge-filter-toggle.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {GlobalNotificationService} from "../../../services/global-notification.service";
import {AbstractLitElement} from "../../abstract-lit.element";

// Side-effect import: registers <o2d-whiteboard> custom element via the
// @customElement decorator. Relies on @o2d/whiteboard's package.json
// declaring index/element files as having side effects.
import "@o2d/whiteboard";
// Stefan, 2026-08-31 — CORRECTION of the 2026-08-29 decision below (kept as
// history, not deleted): the package DOES ship one CSS file again —
// `whiteboard-canvas.css` — and it MUST be imported here. That file holds
// only CANVAS-FUNCTIONAL rules (board-host sizing, the sticky/person/textbox
// edit-mode overlays) — see its own header in o2d-frontend-packages for the
// full split rationale (ADR-013 Nachtrag 2026-08-31) and the standing rule
// for where any FUTURE whiteboard CSS change belongs. Missing this import
// does not just look plainer: it was measured to leave the on-canvas edit
// overlays completely unstyled (no positioning, no font match to the
// Konva-rendered object, WYSIWYG parity broken) — a real, if unreported,
// production gap from 2026-08-30 to 2026-08-31.
//
// Everything else — toolbar, options-circle hover polish, tool cursors —
// stays exactly as the note below already describes: hand-maintained in
// whiteboard.css, no package import, decorative-only so drift there
// only ever looks worse, never breaks.
//
// [Historisch, 2026-08-29 — teilweise überholt durch die Korrektur oben:]
// the package no longer ships CSS at all (its former `whiteboard.css`
// package export/dist copy was removed on the o2d-frontend-packages side;
// the source file now lives outside `src/`, dev/demo-only). ALL whiteboard
// CHROME styling comes exclusively from whiteboard.css (backend/public/
// assets/css/, review-01 2026-08-30 — corrected from an earlier,
// since-outdated reference to ralphy_whiteboard.css, an empty placeholder
// removed 2026-10-17), loaded via spa.css.
import "@o2d/whiteboard/whiteboard-canvas.css";

/** Minimal structural type of the inner whiteboard element used by the consumer. */
type WhiteboardElement = HTMLElement & {
    markSaved(snapshot?: readonly BoardObject[]): void;
    getSnapshot(): WhiteboardChangedEventDetail;
};

/** A lock request still in flight — see `KnowledgeWhiteboard._pendingLockRequest`. */
interface PendingLockRequest {
    uid: string;
    result: Promise<boolean>;
    /**
     * Set by `disconnectedCallback`: it appended the release to `result`, so
     * the caller awaiting `result` must not release a second time.
     */
    releaseChained: boolean;
}

/**
 * Whiteboard rendering pane for a single KnowledgeObject of type `whiteboard`.
 *
 * Wraps `<o2d-whiteboard>` and injects Save as an `InjectedButton`
 * descriptor at the component's own toolbar's left edge, next to the Select
 * tool (O2D-PRD-099; Rename moved to the per-object row menu in
 * `knowledge-object-item.component.ts`, Anke 2026-08-17), with the Knowledge
 * filter-drawer toggle FIRST in that same left-edge group, ahead of Save
 * (Stefan, 2026-08-30 — moved here out of `knowledge-overview-view`'s own,
 * otherwise near-empty toolbar; see `knowledge-filter-toggle.helper.ts`).
 * Loads the
 * KnowledgeObject's content lazily when `item` changes, guards against
 * out-of-order load resolutions via a generation counter, caches the latest
 * `whiteboard-changed` payload into a plain field (no @state — prevents the
 * round-trip re-render that would re-mount Konva and jump the Tiptap cursor),
 * and drives the Save button's enabled state from PRD-011's `isDirty` flag.
 * Persists changes via `KnowledgeObjectRepository.saveContent` and calls
 * `markSaved(payload)` on the whiteboard element to rebase the dirty baseline.
 * Light DOM per ADR-007.
 */
@customElement('knowledge-whiteboard')
export class KnowledgeWhiteboard extends AbstractLitElement implements EditLockHolder {

    @property({type: Object})
    public item: KnowledgeObject | null = null;

    /**
     * Plain field cache of the latest `whiteboard-changed` payload — NO
     * decorator. Reactive tracking would re-render the parent and bounce the
     * payload back into `<o2d-whiteboard>.config`, re-mounting Konva on every
     * edit (the bug PRD-077 fixes).
     */
    public _pendingObjects: readonly BoardObject[] | null = null;

    /** Loaded content payload, bound once to `<o2d-whiteboard>.config`. */
    public _initialContent: readonly unknown[] = [];

    /**
     * The last content the SERVER is known to hold. `_initialContent` is
     * overwritten by an applied draft, so it cannot serve as the revert target
     * of "Abbrechen" or as the dirty baseline of a resumed draft (review
     * 2026-09-19, F1/F2).
     */
    private _serverContent: readonly unknown[] = [];

    /** Track item identity across willUpdate calls to skip redundant loads. */
    private _lastItemUid: string | null = null;

    /**
     * The entity `findOneByIdWithContent` actually returned — carries the
     * live `lockedBy`/`lockedAt` the lock flow reads and mutates. Mirrors
     * `knowledge-rte.component.ts`'s own field — see its doc comment.
     */
    private _loadedKnowledgeObject: KnowledgeObject | null = null;

    /** Render-gate: flips once per item from `false` → `true` after load resolves. */
    @state()
    public _isContentReady = false;

    /** Render-gate for the Save button's `disabled` attribute. Equality-guarded write. */
    @state()
    public _saveButtonEnabled = false;

    @state()
    public _isLoading = false;

    @state()
    public _isSaving = false;

    /**
     * Re-entrancy guard for `_onCancel` (review fix, 2026-09-16), same
     * pattern as `_isSaving` — `_onCancel` awaits a confirm dialog before
     * its first state mutation, so a rapid double-click could otherwise
     * open two confirm dialogs / fire two `releaseLock` calls.
     */
    @state()
    public _isCancelling = false;

    /** Re-entrancy guard for `_onEdit` — see that method's doc comment. */
    private _isEditing = false;

    /**
     * Edit-lock (Anke, 2026-09-15; upgraded to a REAL backend lock, Stefan,
     * 2026-09-15: "den gleichen Lock-Mechanismus... wie bei den
     * Linkview-Kacheln"), mirroring `knowledge-rte.component.ts`'s own
     * `_mode`: the board opens LOCKED (`readonly` on `<o2d-whiteboard>`, all
     * drawing tools hidden — see that property's own doc comment); clicking
     * "Bearbeiten" acquires the lock (or offers to take it over) and shows
     * the tools; Save persists, releases the lock and returns to `'view'` —
     * the User must click "Bearbeiten" again for further edits. See
     * `knowledge-rte.component.ts`'s own `_mode` doc comment for the full
     * reasoning (derivation on load) — identical here. Linkview's
     * `dashboard-creator` follows the same contract since 2026-09-17: its
     * toolbar "Speichern"/"Abbrechen" release the lock and return to view mode.
     */
    @state()
    public _mode: 'view' | 'edit' = 'view';

    /**
     * Ref to call `markSaved(snapshot)` on the inner element after a successful
     * save. The snapshot pins the dirty baseline to the exact payload that was
     * persisted — critical for the concurrent-edit-during-save case where the
     * user keeps editing while the save Promise is in-flight.
     */
    public _wbRef: Ref<WhiteboardElement> = createRef<WhiteboardElement>();

    /**
     * Monotonic counter incremented on every `item` change. Each in-flight
     * `findOneByIdWithContent` call captures the value at dispatch time and
     * discards its result if the counter has moved on (stale-promise guard).
     */
    private _loadGeneration = 0;

    /**
     * The `acquireKnowledgeObjectEditLock` call in flight, from `_onEdit` or
     * the silent resume in `willUpdate` (plain field, nothing renders it).
     * Review 2026-09-19 (K2): the server locks per entity and user, not per
     * element — an unmount while this is pending runs `disconnectedCallback`
     * in 'view' mode, and the lock lands on the server right after (no TTL).
     * `_onEdit` was already covered: its `isConnected === false` branch after
     * the re-read releases the late lock. The silent resume only checked the
     * generation, so ITS lock stayed behind — that is the gap the chain in
     * `disconnectedCallback` closes; for `_onEdit` it is a second safeguard.
     * Mirrors `knowledge-rte.component.ts`.
     */
    private _pendingLockRequest: PendingLockRequest | null = null;

    public async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has('item')) {
            return;
        }
        // Review addition (2026-09-16): persist the OUTGOING item's unsaved
        // state as a draft synchronously (`_flushDraft`, live canvas
        // `getSnapshot()`) before either branch below resets it —
        // `_onChanged`'s 500ms draft debounce and the package's 300ms
        // change debounce could otherwise lose the very last edit if the
        // user clicks a different sidebar entry within that window (this
        // component is reused across items, not remounted, so
        // `disconnectedCallback` does not run here to catch it either).
        if (this._lastItemUid) {
            this._flushDraft(this._lastItemUid);
        }
        const item = this.item;
        if (!item) {
            this._initialContent = [];
            this._pendingObjects = null;
            this._isContentReady = false;
            this._saveButtonEnabled = false;
            this._isLoading = false;
            this._lastItemUid = null;
            this._loadedKnowledgeObject = null;
            // Reset to locked/view: the element is reused across items
            // (knowledge-view-pane), so a lingering 'edit' mode would open
            // the next board directly editable (mirrors knowledge-rte).
            this._mode = 'view';
            return;
        }
        if (item.uid === this._lastItemUid) {
            return;
        }

        // Increment BEFORE any await so that subsequent calls invalidate this one.
        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        this._isLoading = true;
        this._isContentReady = false;
        this._pendingObjects = null;
        this._saveButtonEnabled = false;
        // Switching to a new object always opens it locked, until the load
        // below re-derives it from the object's actual lock state.
        this._mode = 'view';

        const repository = container.resolve(KnowledgeObjectRepository);
        try {
            const loaded = await repository.findOneByIdWithContent(item.uid);
            if (myGeneration !== this._loadGeneration) {
                // A newer load has been kicked off — discard this stale result.
                return;
            }
            this._initialContent = (loaded?.content?.payload ?? []) as readonly unknown[];
            this._serverContent = this._initialContent;
            this._loadedKnowledgeObject = loaded;
            // Held already (e.g. a previous session that never saved/released)
            // -> open directly in edit mode — see `_mode`'s own doc comment.
            this._mode = deriveLocked(loaded?.lockedBy ?? null, appState.user?.uid ?? null) ? 'view' : 'edit';

            // Bug fix (Anke, 2026-09-18: "ich habe ein Dokument in der
            // Sidebar schräg, es ist aber nicht im Bearbeiten-Modus, und im
            // Kopf ist es auch nicht schräg gestellt"): leaving Wissen mode
            // entirely releases the lock (`disconnectedCallback`'s
            // best-effort release, below) but deliberately KEEPS the local
            // draft — discarding it there would silently lose unsaved work,
            // and `disconnectedCallback` cannot await a confirm dialog to
            // ask first. That used to leave a reopened document stranded in
            // plain VIEW mode (no edit tools, header not italic) while the
            // sidebar still showed it as unsaved — the two indicators no
            // longer agreed. A FREE lock with a leftover draft for this uid
            // means nobody else has touched the object since, so silently
            // resuming edit mode here (the exact same `acquireLock` call an
            // explicit "Bearbeiten" click would make) closes that gap. A
            // lock now held by somebody ELSE is left alone: that is a real
            // conflict, and popping the take-over dialog from merely
            // NAVIGATING to the row (not from an explicit "Bearbeiten"
            // click) would be a surprising side effect of just opening it.
            if (this._mode === 'view' && loaded !== null && loaded.lockedBy === null
                && loadWhiteboardDraft(item.uid, this._userUid) !== undefined) {
                const request = this._requestEditLock(loaded);
                const resumed = await request.result;
                if (request.releaseChained) {
                    // Unmounted meanwhile: `disconnectedCallback` gives the lock
                    // back (K2), the mode stays 'view'.
                    return;
                }
                if (myGeneration !== this._loadGeneration) {
                    if (resumed) {
                        this._releaseAbandonedLock(item.uid);
                    }
                    return;
                }
                if (resumed) {
                    this._mode = 'edit';
                }
            }

            this._isContentReady = true;
            this._lastItemUid = item.uid;
            const holdsLock = this._mode === 'edit';
            await this._restoreDraftIfEditing(
                item.uid,
                loaded?.updatedAt?.toISO() ?? '',
                () => myGeneration === this._loadGeneration,
            );
            if (holdsLock && myGeneration !== this._loadGeneration) {
                // Swapped away during the stale-draft dialog: the newer load
                // resets `_mode`, nothing would ever release this lock.
                this._releaseAbandonedLock(item.uid);
            }
        } catch (error) {
            if (myGeneration !== this._loadGeneration) {
                return;
            }
            console.error(`Failed to load whiteboard content for «${item.uid}».`, error);
            this._initialContent = [];
            this._loadedKnowledgeObject = null;
            this._isContentReady = true;
            this._lastItemUid = item.uid;
        } finally {
            if (myGeneration === this._loadGeneration) {
                this._isLoading = false;
            }
        }
    }

    /**
     * Applies a localStorage draft (Anke, 2026-09-16 — "ungespeicherte
     * Änderungen sollen nicht verloren gehen") ONLY when reopening
     * (or re-entering) edit mode — a draft found while the object is in
     * `'view'` mode (lock free or held by somebody else) is left alone: the
     * safe default is to require an explicit "Bearbeiten" click before any
     * local content is applied, rather than silently resurrecting an old
     * edit or reaching for a lock the user never asked for here. See
     * `_onEdit`'s own call site for the second entry point (an explicit
     * "Bearbeiten" click can find a draft too, e.g. left over from a
     * take-over/re-acquire cycle).
     *
     * Review addition (2026-09-17, Anke): a draft is a snapshot from SOME
     * earlier moment — if the object's server-side `updatedAt` has moved on
     * since (someone else saved in the meantime, e.g. after taking over an
     * abandoned lock from a hard-closed tab), blindly applying the draft
     * would silently overwrite their newer content with the stale local
     * one. `baselineUpdatedAt` is what this draft was last written against;
     * a mismatch against the freshly-loaded `currentUpdatedAt` asks instead
     * of silently applying. `isStillCurrent` re-checks after that await —
     * the confirm dialog is itself a gap the item could have been swapped
     * out from under (same class of guard `_onCancel` already has).
     */
    private async _restoreDraftIfEditing(
        uid: string,
        currentUpdatedAt: string,
        isStillCurrent: () => boolean = () => true,
    ): Promise<void> {
        if (this._mode !== 'edit') {
            return;
        }
        const draft = loadWhiteboardDraft(uid, this._userUid);
        if (draft === undefined) {
            return;
        }
        if (draft.baselineUpdatedAt && currentUpdatedAt && draft.baselineUpdatedAt !== currentUpdatedAt) {
            const confirmed = await UiHelper.confirm({
                title: 'Neuere Version vorhanden',
                content: html`<p>Es gibt einen ungespeicherten lokalen Entwurf für dieses
                    Whiteboard, aber das Dokument wurde inzwischen von einer anderen
                    Person gespeichert. Möchten Sie Ihren alten Entwurf trotzdem
                    anwenden (überschreibt die neuere Version) oder verwerfen und mit
                    der aktuellen Version weiterarbeiten?</p>`,
                confirmLabel: 'Entwurf anwenden',
                cancelLabel: 'Verwerfen',
            });
            if (!isStillCurrent()) {
                return;
            }
            if (!confirmed) {
                clearWhiteboardDraft(uid, this._userUid);
                container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
                return;
            }
        }
        if (!isStillCurrent()) {
            return;
        }
        this._initialContent = draft.objects;
        // The draft store types its payload `readonly unknown[]`; its only writers are this
        // component's saveWhiteboardDraft(Now) calls, which store the board's BoardObject arrays.
        this._pendingObjects = draft.objects as readonly BoardObject[];
        this._saveButtonEnabled = true;

        // The package's `config` setter rebases its dirty baseline onto whatever
        // it is handed — here the DRAFT — so its next `whiteboard-changed` would
        // report `isDirty:false`: Save disabled, header not italic, and Cancel
        // discarding the draft without asking (review 2026-09-19, F1, reproduced
        // against `@o2d/whiteboard`). Re-anchor it on the server content once
        // the canvas has taken the draft.
        await this.updateComplete;
        if (isStillCurrent()) {
            this._wbRef.value?.markSaved(this._serverContent as readonly BoardObject[]);
        }
    }

    private get _userUid(): string {
        return appState.user?.uid ?? '';
    }

    private get _draftBaseline(): string {
        return this._loadedKnowledgeObject?.updatedAt?.toISO() ?? '';
    }

    /**
     * Persists the outgoing item's unsaved state as a draft, synchronously.
     * The canvas's own `getSnapshot()` is preferred over `_pendingObjects`,
     * which trails the canvas by the package's 300 ms `whiteboard-changed`
     * debounce — the last edit before a sidebar click would otherwise be lost
     * (review 2026-09-19, F6). A canvas that turned clean again (undo) drops the
     * draft instead of leaving a stale one behind.
     */
    private _flushDraft(uid: string): void {
        const live = this._wbRef.value?.getSnapshot();
        const snapshot = live
            ?? (this._saveButtonEnabled && this._pendingObjects !== null
                ? {objects: this._pendingObjects, isDirty: true}
                : null);
        if (snapshot === null) {
            return;
        }
        if (snapshot.isDirty) {
            saveWhiteboardDraftNow(uid, snapshot.objects, this._draftBaseline, this._userUid);
        } else if (this._saveButtonEnabled) {
            clearWhiteboardDraft(uid, this._userUid);
        } else {
            return;
        }
        container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
    }

    /**
     * A lock acquired by a load/click whose item has since been swapped away
     * must not stay behind (no TTL — only a take-over could recover it). Not
     * released when the SAME uid is bound again on a CONNECTED element: that
     * newer load owns it. An unmounted element never reloads, so it releases
     * even then (review 2026-09-19, L3: A → B with draft → A, then unmount —
     * the older `_onEdit` request was replaced as `_pendingLockRequest`, so
     * `disconnectedCallback` chained nothing to it). Same condition as
     * `knowledge-rte.component.ts`.
     */
    private _releaseAbandonedLock(uid: string): void {
        if (this.isConnected !== false && this.item?.uid === uid) {
            return;
        }
        this._releaseLock(uid);
    }

    private _releaseLock(uid: string): void {
        // Checked HERE, after all awaits: a newer element for the same object
        // may have started editing meanwhile (J1, review 2026-09-19) — the
        // server knows only the user as holder, so releasing would delete the
        // lock that element relies on. Same-user two-TAB case stays open
        // (per-page registry) — fix would be a session id stored with the lock.
        if (anotherHolderHoldsLock(uid, this)) {
            return;
        }
        container.resolve(KnowledgeObjectRepository).releaseLock(uid).catch((releaseError: unknown) => {
            console.error(`Failed to release the abandoned edit lock for «${uid}».`, releaseError);
        });
    }

    /**
     * Starts the lock request and records it for `disconnectedCallback`.
     * Cleared once settled — unless a newer request replaced it meanwhile.
     */
    private _requestEditLock(entity: KnowledgeObject): PendingLockRequest {
        const request: PendingLockRequest = {
            uid: entity.uid,
            result: acquireKnowledgeObjectEditLock(entity),
            releaseChained: false,
        };
        this._pendingLockRequest = request;
        const clear = (): void => {
            if (this._pendingLockRequest === request) {
                this._pendingLockRequest = null;
            }
        };
        // Not `.finally`: that would mint a second, unhandled rejection of the
        // same error next to the one the awaiting caller already gets.
        request.result.then(clear, clear);
        return request;
    }

    /**
     * Re-reads the object so `updatedAt` (the draft baseline) and the content
     * are current, not the values of its first load (review 2026-09-19, F2).
     * Best effort: a failed read keeps what is there.
     */
    private async _reloadLoadedEntity(uid: string, generation: number): Promise<void> {
        let fresh: KnowledgeObject | null = null;
        try {
            fresh = await container.resolve(KnowledgeObjectRepository).findOneByIdWithContent(uid) ?? null;
        } catch (error) {
            console.error(`Failed to refresh whiteboard «${uid}».`, error);
        }
        if (fresh === null || generation !== this._loadGeneration) {
            return;
        }
        this._loadedKnowledgeObject = fresh;
        const freshContent = (fresh.content?.payload ?? []) as readonly unknown[];
        // A new array reference re-runs the package's `config` setter (viewport
        // reset), so only swap when the server content really changed.
        if (JSON.stringify(freshContent) !== JSON.stringify(this._serverContent)) {
            this._serverContent = freshContent;
            this._initialContent = freshContent;
        }
    }

    /**
     * Handles the inner `<o2d-whiteboard>` `whiteboard-changed` CustomEvent.
     * Caches the payload by reference into the plain field `_pendingObjects`
     * (no defensive copy — PRD-011 ships the array as readonly), and writes
     * `_saveButtonEnabled` ONLY when the boolean actually flips so the
     * 300ms-debounced changed events do not trigger a render storm.
     *
     * Review addition (2026-09-16): every dirty change also debounce-saves
     * a browser-local draft, keyed by this object's uid — see
     * `whiteboard-draft-store.helper.ts`. Without this, switching to a
     * different sidebar entry without saving silently lost the edit (the
     * component is reused across items — `willUpdate` wipes
     * `_pendingObjects` for the newly-bound item, and the OLD item's edits
     * only ever lived in that plain field, never anywhere durable).
     *
     * Bug fix (Anke, 2026-09-18: "MUSS ... schräg gestellt werden und mit
     * Sternchen ... im Moment klappt das erst, wenn ich auf ein anderes
     * Dokument klicke"): the sidebar's unsaved-indicator re-reads
     * `localStorage` synchronously as soon as `KnowledgeDraftChangeNotifier`
     * fires — but `saveWhiteboardDraft` above is 500ms-debounced, so a
     * `notify()` fired on EVERY dirty event (as this used to do
     * unconditionally) almost always raced ahead of the write it was meant
     * to announce, and nothing ever re-notified once the debounced write
     * actually landed. Only `willUpdate`'s item-switch flush paired a
     * synchronous write with its notify correctly, which is why the
     * indicator only ever appeared to update on switching documents. Fixed
     * by writing (and notifying) SYNCHRONOUSLY, bypassing the debounce, the
     * FIRST time this edit session turns dirty — subsequent keystrokes keep
     * debouncing as before (the indicator is already correct, and the
     * debounce still protects `localStorage` from a write per canvas event).
     */
    public _onChanged = (event: CustomEvent<WhiteboardChangedEventDetail>): void => {
        this._pendingObjects = event.detail.objects;
        const becameDirty = !this._saveButtonEnabled && event.detail.isDirty;
        const becameClean = this._saveButtonEnabled && !event.detail.isDirty;
        if (this._saveButtonEnabled !== event.detail.isDirty) {
            this._saveButtonEnabled = event.detail.isDirty;
        }
        const uid = this.item?.uid;
        if (uid && event.detail.isDirty) {
            if (becameDirty) {
                saveWhiteboardDraftNow(uid, event.detail.objects, this._draftBaseline, this._userUid);
                container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
            } else {
                saveWhiteboardDraft(uid, event.detail.objects, this._draftBaseline, this._userUid);
            }
        } else if (uid && becameClean) {
            // Undo back to the saved state: a still-pending debounced write would
            // otherwise resurrect the dirty intermediate, and the stored draft
            // keeps the sidebar starred (review 2026-09-19, F3).
            cancelPendingWhiteboardDraft(uid, this._userUid);
            if (loadWhiteboardDraft(uid, this._userUid) !== undefined) {
                clearWhiteboardDraft(uid, this._userUid);
                container.resolve(KnowledgeDraftChangeNotifier).notify(uid);
            }
        }
    };

    /**
     * Unlocks the board for editing. Order: acquire (or offer to take over)
     * the lock via `acquireKnowledgeObjectEditLock()` → re-read the object →
     * `_mode = 'edit'` → `_restoreDraftIfEditing` (which asks before applying
     * a draft whose baseline is stale). Unlike `knowledge-rte.component.ts`'s
     * `_onEdit`, which asks BEFORE it switches into edit mode (its editor
     * restores drafts on mount, so the question must come first). The
     * `.readonly=${this._mode === 'view'}` binding in `render()` propagates
     * the mode change to `<o2d-whiteboard>` reactively.
     *
     * `_isEditing` covers the whole run (lock request, re-read, dialogs): a
     * second click meanwhile would stack a second stale-draft dialog whose
     * answer can contradict the first (review 2026-09-19, H1).
     */
    public _onEdit = async (): Promise<void> => {
        const entity = this._loadedKnowledgeObject;
        if (!entity || this._isLoading || this._isSaving || this._isCancelling || this._isEditing) {
            return;
        }
        this._isEditing = true;
        try {
            const editGeneration = this._loadGeneration;

            const request = this._requestEditLock(entity);
            const acquired = await request.result;
            if (!acquired) {
                return;
            }
            if (request.releaseChained) {
                // Unmounted while the request was pending: `disconnectedCallback`
                // chained the release onto it (K2) — not a second one here.
                return;
            }
            if (editGeneration !== this._loadGeneration) {
                // The item was swapped while the lock request / take-over dialog was
                // pending: `_mode` now belongs to the NEW item, the lock to the old.
                this._releaseAbandonedLock(entity.uid);
                return;
            }
            // Read after the lock is held, so nobody can save between this read and
            // the stale-draft comparison below.
            await this._reloadLoadedEntity(entity.uid, editGeneration);
            if (editGeneration !== this._loadGeneration) {
                this._releaseAbandonedLock(entity.uid);
                return;
            }
            // Unmounted while the READ was pending (the lock request had already
            // settled, so nothing was chained to it): `disconnectedCallback` ran
            // in `'view'` mode and released nothing, and `this.item` is still
            // this uid, so `_releaseAbandonedLock` would skip it too. `=== false`
            // because test doubles of the base class carry no `isConnected`.
            if (this.isConnected === false) {
                this._releaseLock(entity.uid);
                return;
            }
            this._mode = 'edit';
            await this._restoreDraftIfEditing(
                entity.uid,
                this._loadedKnowledgeObject?.updatedAt?.toISO() ?? '',
                () => editGeneration === this._loadGeneration,
            );
            if (editGeneration !== this._loadGeneration) {
                this._releaseAbandonedLock(entity.uid);
            }
        } finally {
            this._isEditing = false;
        }
    };

    /**
     * Persists the cached `_pendingObjects` (falling back to `_initialContent`
     * when no edits happened yet) via the repository. On success rebases the
     * whiteboard's dirty baseline via `markSaved(payload)` passing the EXACT
     * persisted snapshot — any edits made during the in-flight save stay
     * surfaced as dirty against the new baseline — and returns to LOCKED
     * view mode (mirrors knowledge-rte's `_onSave`: the User must click
     * "Bearbeiten" again for further edits). On rejection surfaces a
     * notification and preserves cache + button state (AND stays in edit
     * mode) so the user can retry.
     */
    public _onSave = async (): Promise<void> => {
        const item = this.item;
        if (!item || this._isSaving || this._isLoading) {
            return;
        }
        if (!this._saveButtonEnabled && this._pendingObjects === null) {
            // Defensive — button should already be disabled when nothing is pending.
            return;
        }
        this._isSaving = true;
        const saveGeneration = this._loadGeneration;
        const payload = (this._pendingObjects ?? this._initialContent ?? []) as readonly BoardObject[];
        try {
            const repository = container.resolve(KnowledgeObjectRepository);
            await repository.saveContent(item.uid, payload);
            // ORDER MATTERS: rebase baseline FIRST (so any further changed-emit
            // reports against the new baseline), then clear cache, then flip
            // the button. Pass `payload` explicitly to markSaved — the canvas
            // is NOT locked during save, so the user may have produced edits
            // while saveContent was in-flight; anchoring to the original
            // payload keeps those unsaved edits surfaced.
            this._wbRef.value?.markSaved(payload);
            this._pendingObjects = null;
            this._saveButtonEnabled = false;
            clearWhiteboardDraft(item.uid, this._userUid);
            container.resolve(KnowledgeDraftChangeNotifier).notify(item.uid);
            // Release the lock and return to LOCKED view mode (Anke's explicit
            // requirement — see `_mode`'s own doc comment). A failed release
            // is logged but not surfaced: the content is already safely
            // saved, and a stray un-released lock just means the User
            // re-enters via "Bearbeiten" once more.
            // ORDER (review fix, 2026-09-16): await the release BEFORE
            // flipping `_mode` — flipping first made this client's own UI
            // show "free" while the server lock could still be held (a
            // failed/slow release), which could surface a misleading
            // take-over dialog to a second user racing in during that gap.
            await repository.releaseLock(item.uid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${item.uid}» after saving.`, releaseError);
            });
            this._mode = 'view';
            if (saveGeneration === this._loadGeneration) {
                this._serverContent = payload;
                // The save moved the server's `updatedAt`; a stale one here made
                // the next draft look "saved by somebody else" (review F2).
                await this._reloadLoadedEntity(item.uid, saveGeneration);
            }
        } catch (error) {
            if (error instanceof LockConflictError) {
                // Somebody took the lock over WHILE this save was in flight —
                // the repository already refreshed `_loadedKnowledgeObject`'s
                // lock fields before this rejection settled.
                this._mode = 'view';
                container.resolve(GlobalNotificationService).error(
                    `Das Whiteboard wurde von einer anderen Person übernommen.`,
                    `Ihre Änderungen seit der Übernahme wurden nicht gespeichert.`,
                );
            } else {
                console.error(`Failed to save whiteboard content for «${item.uid}».`, error);
                container.resolve(GlobalNotificationService).error(
                    `Whiteboard konnte nicht gespeichert werden.`,
                    error instanceof Error ? error.message : undefined,
                );
            }
        } finally {
            this._isSaving = false;
        }
    };

    /**
     * Review fix (2026-09-16): a best-effort release for the "navigated away
     * while still in edit mode" gap — neither `_onSave` nor `_onCancel` runs
     * when the host simply unmounts this element (e.g. the user switches to
     * a different item without saving/cancelling). Fire-and-forget:
     * `disconnectedCallback` cannot be async/awaited, and this is a best
     * effort only — it will not fire on a hard tab close/crash either, same
     * limitation the existing Linkview lock has.
     */
    public connectedCallback(): void {
        super.connectedCallback();
        registerEditLockHolder(this);
    }

    /** See `edit-lock-holders.helper.ts`: lets a replaced sibling ask whether this element edits `uid`. */
    public holdsEditLockFor(uid: string): boolean {
        return this.item?.uid === uid && this.isConnected !== false && this._mode === 'edit';
    }

    public disconnectedCallback(): void {
        unregisterEditLockHolder(this);
        if (this._mode === 'edit' && this.item) {
            if (!anotherHolderHoldsLock(this.item.uid, this)) {
                container.resolve(KnowledgeObjectRepository).releaseLock(this.item.uid).catch((releaseError: unknown) => {
                    console.error(`Failed to release the edit lock for «${this.item?.uid}» on disconnect.`, releaseError);
                });
            }
            // Same flush as `willUpdate`'s item-switch guard — this only
            // additionally covers the "closed Wissen mode entirely" unmount
            // path, since item-switching within Wissen mode never actually
            // unmounts this component (see that guard's own comment).
            this._flushDraft(this.item.uid);
        }
        // A lock request still pending lands on the server AFTER this ran in
        // 'view' mode (K2: "Bearbeiten" or a draft resume, then an immediate
        // switch to the other content type replaces this element). The caller
        // awaiting it sees `releaseChained` and leaves the release to this
        // chain — one release per acquired lock. `uid` comes from the request,
        // not from `this.item` at settle time.
        const pending = this._pendingLockRequest;
        if (pending) {
            pending.releaseChained = true;
            pending.result.then(
                (acquired) => {
                    if (acquired) {
                        this._releaseLock(pending.uid);
                    }
                },
                () => { /* nothing was acquired; the awaiting caller reports the failure */ },
            );
        }
        super.disconnectedCallback();
    }

    /**
     * Discards any unsaved edit and returns to LOCKED view mode without
     * saving (Anke, 2026-09-16 — "es fehlt ein Abbrechen-Button, wenn man
     * gar nichts ändern möchte").
     *
     * Asks for confirmation FIRST when there is something to lose
     * (`_saveButtonEnabled`, mirroring the Save button's own dirty gate) —
     * declining leaves everything untouched, still in edit mode. When
     * confirmed (or when there was nothing dirty to begin with):
     *
     * 1. Reassign `_initialContent` to a NEW array reference holding the
     *    server's last-known payload (`_serverContent` — NOT the old
     *    `_initialContent`, which an applied draft overwrites). The
     *    `.config=${this._initialContent}` binding compares by identity —
     *    without a fresh reference the setter would not re-fire at all. The
     *    package's `config` setter is documented as the atomic
     *    content-replacement entry point (`o2d-whiteboard.element.ts`'s own
     *    doc comment): it replaces the live board's objects, clears
     *    history and re-fits the viewport WITHOUT remounting Konva — no
     *    `keyed()` wrapper needed or wanted here (that would defeat the
     *    whole anti-remount design this component's own class doc explains).
     * 2. Release the lock — mirrors `_onSave`'s own release-on-exit, same
     *    "must click Bearbeiten again" contract either way out of edit mode.
     */
    public _onCancel = async (): Promise<void> => {
        const item = this.item;
        if (!item || this._isSaving || this._isLoading || this._isCancelling) {
            return;
        }
        // Review fix (2026-09-16): captured BEFORE the first await, so the
        // revert writes below can detect whether `willUpdate` has since
        // moved on to a different item (e.g. the host swapped `item` while
        // this Cancel was still in flight) and skip clobbering that newer
        // load's state — same "stale-promise guard" pattern `willUpdate`
        // itself already uses for `_loadGeneration`.
        const cancelGeneration = this._loadGeneration;
        this._isCancelling = true;
        try {
            if (this._saveButtonEnabled) {
                const confirmed = await UiHelper.confirm({
                    title: get('wissen.whiteboard.cancel-confirm-title'),
                    content: html`<p>${get('wissen.whiteboard.cancel-confirm-content')}</p>`,
                    confirmLabel: get('wissen.whiteboard.cancel-confirm-confirm'),
                    cancelLabel: get('wissen.whiteboard.cancel-confirm-cancel'),
                });
                if (!confirmed) {
                    // Review fix (2026-09-16): the confirm dialog itself was an
                    // async gap during which another user could have taken the
                    // lock over. Re-verify by re-acquiring — re-acquiring one's
                    // OWN lock is a documented no-op (LockEntityCommandHandler),
                    // so this either confirms nothing changed, or surfaces a
                    // real conflict instead of silently staying in a stale
                    // edit mode with a Save button that would only fail later.
                    const repository = container.resolve(KnowledgeObjectRepository);
                    await repository.acquireLock(item.uid).catch((error: unknown) => {
                        // Review fix (2026-09-16, review-02): guard the write
                        // by generation too — the confirm dialog's own await
                        // is a second async gap the item could have been
                        // swapped out from under, same reasoning as the
                        // generation guard below.
                        if (cancelGeneration !== this._loadGeneration) {
                            return;
                        }
                        if (error instanceof LockConflictError) {
                            this._mode = 'view';
                            container.resolve(GlobalNotificationService).error(
                                `Das Whiteboard wurde von einer anderen Person übernommen.`,
                                `Ihre Änderungen seit der Übernahme wurden nicht gespeichert.`,
                            );
                        } else {
                            console.error(`Failed to re-verify the edit lock for «${item.uid}» after a declined cancel.`, error);
                        }
                    });
                    return;
                }
            }

            // Review fix (2026-09-16, review-02): the ORIGINAL bug here —
            // this generation check used to gate the `releaseLock` call
            // itself, so a stale Cancel (item swapped mid-flight) skipped
            // releasing the ORIGINAL item's lock entirely, leaking it.
            // `releaseLock(item.uid)` below now ALWAYS runs regardless of
            // generation — an abandoned edit must not leak its lock just
            // because the user navigated away before this settled. Only the
            // LOCAL STATE writes (this component's own `_initialContent`/
            // `_pendingObjects`/`_saveButtonEnabled`/`_mode`) are guarded,
            // since a fresher `willUpdate` load already owns them for
            // whatever is now loaded.
            if (cancelGeneration === this._loadGeneration) {
                this._initialContent = [...this._serverContent];
                this._pendingObjects = null;
                this._saveButtonEnabled = false;
            }
            // Discard the local draft for the ORIGINAL item unconditionally
            // (not generation-guarded) — "Abbrechen" means the user does not
            // want these changes, regardless of whether the host has since
            // switched to a different item. Mirrors `knowledge-rte.component
            // .ts`'s own `discardDraft()` call in the same branch.
            clearWhiteboardDraft(item.uid, this._userUid);
            container.resolve(KnowledgeDraftChangeNotifier).notify(item.uid);

            // ORDER (review fix, 2026-09-16): await the release BEFORE
            // flipping `_mode` — see `_onSave`'s matching comment for why.
            await container.resolve(KnowledgeObjectRepository).releaseLock(item.uid).catch((releaseError: unknown) => {
                console.error(`Failed to release the edit lock for «${item.uid}» after cancelling.`, releaseError);
            });
            if (cancelGeneration === this._loadGeneration) {
                this._mode = 'view';
            }
        } finally {
            this._isCancelling = false;
        }
    };

    /**
     * Returns the next free `<title>-<number>` suffix: the highest number already
     * used by an existing title plus one, or 1 when none exists. The title is
     * escaped before it becomes a pattern, so a whiteboard named `A-B` or `A+B`
     * is matched literally instead of as a regular expression.
     *
     * @param title Whiteboard title the generated names are derived from.
     * @param existing Candidate objects whose titles may already carry a suffix.
     * @returns The number to append to the next generated title.
     */
    private _nextImageNumber(title: string, existing: readonly { title: string }[]): number {
        const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`);
        const numberPattern = new RegExp(`^${escapedTitle}-(\\d+)$`);

        let highest = 0;
        for (const candidate of existing) {
            const match = numberPattern.exec(candidate.title ?? ``);
            if (match && Number(match[1]) > highest) {
                highest = Number(match[1]);
            }
        }

        return highest + 1;
    }

    /**
     * Answers the whiteboard's question how to paste a clipboard holding BOTH text and an image
     * (Anke, 2026-10-02 — PowerPoint, Word and Excel put a rendered PNG next to the text).
     * The primary button (Enter, initial focus) is "Nur den Text": what Anke asked for was real
     * text instead of a picture of it. Closing the dialog pastes nothing.
     */
    public _onPasteChoice = async (): Promise<PasteChoice | null> => {
        const choice = await UiHelper.choose({
            title: get('wissen.whiteboard.paste-choice-title'),
            content: html`<p>${get('wissen.whiteboard.paste-choice-content')}</p>`,
            confirmLabel: get('wissen.whiteboard.paste-choice-text'),
            cancelLabel: get('wissen.whiteboard.paste-choice-image'),
        });
        if (choice === 'confirm') {
            return 'text';
        }
        if (choice === 'cancel') {
            return 'image';
        }
        return null;
    };

    /**
     * Uploads a pasted clipboard image and returns the reference the whiteboard
     * places on the canvas. The bytes are stored FIRST, so a failed upload
     * leaves nothing behind; only then is the sibling image KnowledgeObject
     * created (named `<Whiteboard-Titel>-<Nummer>`, continuing from the highest
     * existing suffix) and its content written. A failing content write rolls
     * the just-created object back — an image KO without content can never
     * render. Every failure is surfaced to the user AND re-thrown, because the
     * package only places the image once this Promise resolves.
     */
    public _onImagePasteUpload = async (file: File): Promise<{ src: string }> => {
        const item = this.item;
        if (!item) {
            throw new Error(`Cannot upload a pasted image without a whiteboard item.`);
        }
        try {
            const asset = await container.resolve(BackendClient).uploadFile(file);
            const repository = container.resolve(KnowledgeObjectRepository);

            // The upload deduplicates by checksum, so pasting the same picture
            // again hands back the asset that already exists. Creating a second
            // image object for it would split one picture's references across
            // two identities — what is embedded and counted is the object. So
            // the object that already holds these bytes is embedded instead.
            const alreadyStored = await repository.findImageObjectUidByAsset(asset.uid);
            if (alreadyStored !== null) {
                return {src: buildAssetEmbedSrc(alreadyStored)};
            }

            const existing = await repository.findAllFiltered({type: `image`});
            const nextNumber = this._nextImageNumber(item.title, existing);

            const created = await repository.create(`${item.title}-${String(nextNumber)}`, `image`);
            // Mirrors the non-exported `ImageContentItem` of the create-dialog
            // blueprint (`knowledge-object-edit-submit.ts:37-42`) — keep both in sync.
            const content = {
                kind: `image` as const,
                assetUid: asset.uid,
                mime: asset.mime,
                sizeBytes: asset.sizeBytes,
            };
            try {
                await repository.saveContent(created.uid, [content]);
            } catch (error) {
                // Roll the orphan back, but never let a failing rollback mask
                // the original cause — the user must see why the paste failed.
                await repository.delete(created).catch(() => { /* keep original error */ });
                throw error;
            }

            return {src: buildAssetEmbedSrc(created.uid)};
        } catch (error) {
            console.error(`Failed to upload pasted image for «${item.uid}».`, error);
            container.resolve(GlobalNotificationService).error(
                `Bild konnte nicht hochgeladen werden.`,
                error instanceof Error ? error.message : undefined,
            );
            throw error;
        }
    };

    /**
     * Routes an `injected-button-click` from the inner `<o2d-whiteboard>` toolbar
     * to the corresponding action by the descriptor `id`. `AbstractO2dElement`
     * stamps a `timestamp` onto the detail, so the CustomEvent detail carries
     * `{ timestamp, id }`. Arrow function to match the file's handler idiom.
     */
    public _onInjectedButtonClick = (event: CustomEvent<{ timestamp: number; id: string }>): void => {
        switch (event.detail.id) {
            case 'edit':
                void this._onEdit();
                break;
            case 'save':
                void this._onSave();
                break;
            case 'cancel':
                void this._onCancel();
                break;
            case 'filter':
                toggleKnowledgeFilterDrawer();
                break;
        }
    };

    /** @internal Public only so unit tests can walk the rendered template; Lit itself calls it. */
    public render(): HTMLTemplateResult | typeof nothing {
        if (!this.item) {
            return nothing;
        }

        const saveDisabled = !this._saveButtonEnabled || this._isLoading || this._isSaving;
        const editLabel = get('wissen.whiteboard.edit');
        const saveLabel = get('wissen.whiteboard.save');
        const cancelLabel = get('wissen.whiteboard.cancel');
        const filterLabel = get('toolbar.filter');

        // Recompute NEW array references each render so Lit's identity
        // `hasChanged` re-renders `<o2d-whiteboard>` when `saveDisabled` flips
        // (replace-not-mutate; the injected buttons are consumed via an
        // `@property({ attribute: false })` on the component side). Icons are the
        // the literal Font-Awesome Unicode glyph, not an icon name — see
        // `InjectedButton.icon`'s docstring.
        // Rendered at the toolbar's LEFT edge, next to the Select tool
        // (Anke, 2026-08-17) — the filter-drawer toggle (Stefan, 2026-08-30)
        // sits FIRST. Edit-lock (Anke, 2026-09-15, mirrors knowledge-rte):
        // Edit XOR Save follows the filter toggle, by `_mode` — never both at
        // once, same as knowledge-rte's ternary'd editButton/saveButton.
        // `group: 'edit'` (2026-09-19): Bearbeiten/Speichern and Abbrechen share
        // ONE `div.button-group` in the whiteboard toolbar (consecutive buttons
        // with the same `group` merge; the filter toggle keeps its own group).
        // Rename moved to the per-object row menu
        // (knowledge-object-item.component.ts).
        const injectedButtonsStart: InjectedButton[] = this._mode === 'view'
            ? [
                {id: 'filter', label: filterLabel, icon: '', disabled: false, variant: 'secondary'},
                {id: 'edit', label: editLabel, icon: '', disabled: false, group: 'edit'},
            ]
            : [
                {id: 'filter', label: filterLabel, icon: '', disabled: false, variant: 'secondary'},
                {id: 'save', label: saveLabel, icon: '', disabled: saveDisabled, group: 'edit'},
                // "Abbrechen" (Anke, 2026-09-16): discards the edit and
                // releases the lock without saving — see `_onCancel`'s own
                // doc comment. Never disabled by dirty state (unlike Save):
                // cancelling out of a CLEAN edit session is exactly the "I
                // clicked Bearbeiten but didn't want to change anything"
                // case this button exists for.
                {id: 'cancel', label: cancelLabel, icon: '', disabled: this._isLoading || this._isSaving || this._isCancelling, variant: 'secondary', group: 'edit'},
            ];

        const canvas = this._isContentReady
            ? html`<o2d-whiteboard
                    ${ref(this._wbRef)}
                    class="knowledge-whiteboard__canvas"
                    .config=${this._initialContent}
                    .imageUploadHandler=${this._onImagePasteUpload}
                    .pasteChoiceHandler=${this._onPasteChoice}
                    .injectedButtonsStart=${injectedButtonsStart}
                    .fileName=${this.item.title}
                    .fileNameUnsaved=${this._mode === 'edit' && this._saveButtonEnabled}
                    .readonly=${this._mode === 'view'}
                    @whiteboard-changed=${this._onChanged}
                    @injected-button-click=${this._onInjectedButtonClick}
                ></o2d-whiteboard>`
            : html`<div class="knowledge-whiteboard__loading"></div>`;

        return html`${canvas}`;
    }
}
